// Module 6 — Management Portal (MP-01 to MP-05)
// Extends ManagementDashboard.js with drill-downs, scenario analysis, exception reporting
import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../../components/layout/AppShell';
import api from '../../services/api';
import { fmtPKR, fmtNum, fmtDate } from '../../utils/helpers';
import { Spinner, EmptyState } from '../../components/common';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Edit2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';

const COLORS = ['#1B2A4A','#F59E0B','#10B981','#EF4444','#3B82F6','#8B5CF6','#EC4899'];

// ── MP-02 Revenue Analytics ───────────────────────────────────────────────────
export function RevenueAnalyticsPage() {
  const [data, setData]   = useState(null);
  const [loading, setLoad] = useState(true);
  const [range, setRange] = useState({ from: new Date(Date.now()-30*864e5).toISOString().slice(0,10), to: new Date().toISOString().slice(0,10), groupBy:'day' });

  const load = useCallback(async () => {
    setLoad(true);
    try {
      const r = await api.get('/finance/revenue-report?fromDate='+range.from+'&toDate='+range.to+'&groupBy='+range.groupBy).then(r=>r.data);
      if (r.success) setData(r.data);
    } finally { setLoad(false); }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const totals = (data||[]).reduce((s,d) => ({
    revenue: s.revenue + parseFloat(d.gross_revenue||0),
    shipments: s.shipments + parseInt(d.shipments||0),
    delivered: s.delivered + parseInt(d.delivered||0),
    rto: s.rto + parseInt(d.rto||0),
  }), { revenue:0, shipments:0, delivered:0, rto:0 });

  return (
    <AppShell title="Revenue Analytics" actions={
      <div style={{ display:'flex', gap:8, alignItems:'center' }}>
        <input className="form-control" type="date" style={{ width:140 }} value={range.from} onChange={e=>setRange(r=>({...r,from:e.target.value}))}/>
        <input className="form-control" type="date" style={{ width:140 }} value={range.to}   onChange={e=>setRange(r=>({...r,to:e.target.value}))}/>
        <select className="form-control" style={{ width:100 }} value={range.groupBy} onChange={e=>setRange(r=>({...r,groupBy:e.target.value}))}>
          <option value="day">Daily</option><option value="week">Weekly</option><option value="month">Monthly</option>
        </select>
        <button className="btn btn-primary btn-sm" onClick={load}>Apply</button>
      </div>
    }>
      <div className="stat-grid" style={{ marginBottom:20 }}>
        <div className="stat-card"><div className="stat-label">Gross Revenue</div><div className="stat-value" style={{ color:'var(--success)' }}>{fmtPKR(totals.revenue)}</div></div>
        <div className="stat-card"><div className="stat-label">Total Shipments</div><div className="stat-value">{fmtNum(totals.shipments)}</div></div>
        <div className="stat-card"><div className="stat-label">Delivered</div><div className="stat-value" style={{ color:'var(--success)' }}>{fmtNum(totals.delivered)}</div></div>
        <div className="stat-card"><div className="stat-label">RTO</div><div className="stat-value" style={{ color:'var(--danger)' }}>{fmtNum(totals.rto)}</div></div>
        <div className="stat-card">
          <div className="stat-label">Delivery Rate</div>
          <div className="stat-value" style={{ color: totals.shipments>0&&(totals.delivered/totals.shipments*100)>=95?'var(--success)':'var(--warning)' }}>
            {totals.shipments>0 ? (totals.delivered/totals.shipments*100).toFixed(1)+'%' : '—'}
          </div>
        </div>
        <div className="stat-card"><div className="stat-label">Rev per Shipment</div><div className="stat-value">{totals.shipments>0?fmtPKR(totals.revenue/totals.shipments):'—'}</div></div>
      </div>

      <div className="card" style={{ marginBottom:16 }}>
        <div className="card-header"><span className="card-title">Revenue Trend</span></div>
        <div className="card-body">
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data||[]}>
              <defs><linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3}/><stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/></linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
              <XAxis dataKey="period" tick={{ fontSize:11 }} tickFormatter={d=>d?.slice?d.slice(5,10):d}/>
              <YAxis tick={{ fontSize:11 }} tickFormatter={v=>'PKR '+Math.round(v/1000)+'K'}/>
              <Tooltip formatter={v=>[fmtPKR(v),'Revenue']}/>
              <Area type="monotone" dataKey="gross_revenue" name="Revenue" stroke="#F59E0B" fill="url(#revGrad)" strokeWidth={2}/>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">Volume vs Revenue by Period</span></div>
        <div className="table-wrap">
          {loading ? <Spinner /> : (data||[]).length ? (
            <table>
              <thead><tr><th>Period</th><th>Shipments</th><th>Delivered</th><th>RTO</th><th>Gross Revenue</th><th>Freight</th><th>COD Fees</th><th>Tax</th></tr></thead>
              <tbody>
                {(data||[]).map((d,i)=>(
                  <tr key={i}>
                    <td style={{ fontFamily:'monospace', fontSize:'.8rem' }}>{d.period?.slice?d.period.slice(0,10):d.period}</td>
                    <td>{fmtNum(d.shipments)}</td>
                    <td style={{ color:'var(--success)' }}>{fmtNum(d.delivered)}</td>
                    <td style={{ color:'var(--danger)' }}>{fmtNum(d.rto)}</td>
                    <td style={{ fontWeight:600 }}>{fmtPKR(d.gross_revenue)}</td>
                    <td>{fmtPKR(d.freight)}</td>
                    <td>{fmtPKR(d.cod_fees)}</td>
                    <td>{fmtPKR(d.tax)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="📊" title="No revenue data for selected period" />}
        </div>
      </div>
    </AppShell>
  );
}

// ── MP-03 Shipment Performance Dashboard ─────────────────────────────────────
export function ShipmentPerformancePage() {
  const [data, setData]   = useState(null);
  const [loading, setLoad] = useState(true);
  useEffect(() => {
    Promise.all([
      api.get('/dashboard/executive').then(r=>r.data).catch(()=>({ success:false })),
      api.get('/shipments?limit=1&status=DELIVERY_ATTEMPTED').then(r=>r.data).catch(()=>({ success:false })),
    ]).then(([exec, delayed]) => {
      if (exec.success) setData(exec.data);
    }).finally(()=>setLoad(false));
  }, []);

  if (loading) return <AppShell title="Shipment Performance"><Spinner /></AppShell>;

  const sr = data?.deliverySuccessRate || 0;
  const pipeline = (data?.volumes||[]).reduce((m,v)=>({ ...m, [v.status]:parseInt(v.count||0) }), {});

  const pieData = Object.entries(pipeline).map(([k,v]) => ({ name:k.replace(/_/g,' '), value:v })).filter(d=>d.value>0);

  return (
    <AppShell title="Shipment Performance">
      <div className="stat-grid" style={{ marginBottom:20 }}>
        {[
          ['Delivery Success', sr+'%', sr>=95?'var(--success)':sr>=85?'var(--warning)':'var(--danger)', 'Target ≥ 95%'],
          ['Delayed', data?.exceptions?.delayed||0, 'var(--warning)', 'Active exceptions'],
          ['Undelivered', data?.exceptions?.undelivered||0, 'var(--danger)', '3+ attempts'],
          ['Damaged', data?.exceptions?.damaged||0, 'var(--danger)', 'In network'],
          ['Lost', data?.exceptions?.lost||0, '#7f1d1d', 'Active cases'],
          ['Scan Gaps', data?.exceptions?.scan_gaps||0, 'var(--amber)', 'Missing events'],
        ].map(([l,v,c,s]) => (
          <div key={l} className="stat-card" style={{ borderTop:'3px solid '+c }}>
            <div className="stat-label">{l}</div>
            <div style={{ fontWeight:700, fontSize:'1.2rem', color:c }}>{typeof v==='number'?fmtNum(v):v}</div>
            <div style={{ fontSize:'.7rem', color:'var(--gray-400)', marginTop:3 }}>{s}</div>
          </div>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>
        <div className="card">
          <div className="card-header"><span className="card-title">Pipeline Distribution</span></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart><Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({name,value})=>name.slice(0,8)+' '+value}>
                {pieData.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
              </Pie><Tooltip/></PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="card">
          <div className="card-header"><span className="card-title">7-Day Delivery Rate</span></div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={data?.trend||[]}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
                <XAxis dataKey="date" tick={{ fontSize:11 }} tickFormatter={d=>d?.slice(5)}/>
                <YAxis tick={{ fontSize:11 }} domain={[0,100]} tickFormatter={v=>v+'%'}/>
                <Tooltip formatter={v=>[v+'%']}/>
                <Line type="monotone" dataKey={(d)=>d.total>0?((d.delivered/d.total)*100).toFixed(1):0} name="Delivery Rate" stroke="#10B981" strokeWidth={2} dot={{r:3}}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

// ── MP-04 Exception Report ────────────────────────────────────────────────────
export function ExceptionReportPage() {
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoad] = useState(true);
  const [filter, setFilter] = useState('ALL');

  useEffect(() => {
    api.get('/shipments?status=DELIVERY_ATTEMPTED,LOST,DAMAGED,DELAYED&limit=100').then(r=>r.data)
      .then(r=>{ if(r.success) setExceptions(r.data?.shipments||[]); })
      .finally(()=>setLoad(false));
  }, []);

  const filtered = filter === 'ALL' ? exceptions : exceptions.filter(e => e.status === filter);
  const STATUS_COLOR = { DELIVERY_ATTEMPTED:'var(--warning)', LOST:'var(--danger)', DAMAGED:'#7f1d1d', CANCELLED:'var(--gray-400)' };

  return (
    <AppShell title="Exception Report">
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        {['ALL','DELIVERY_ATTEMPTED','LOST','DAMAGED','CANCELLED'].map(s=>(
          <button key={s} className={'btn btn-sm '+(filter===s?'btn-primary':'btn-outline')} onClick={()=>setFilter(s)}>
            {s.replace(/_/g,' ')} {s!=='ALL'&&<span style={{ marginLeft:4 }}>({exceptions.filter(e=>e.status===s).length})</span>}
          </button>
        ))}
        <span style={{ marginLeft:'auto', fontSize:'.82rem', color:'var(--gray-400)', alignSelf:'center' }}>{filtered.length} exceptions</span>
      </div>
      <div className="card">
        <div className="table-wrap">
          {loading ? <Spinner /> : filtered.length ? (
            <table>
              <thead><tr><th>Tracking ID</th><th>Status</th><th>Service</th><th>From</th><th>To</th><th>EDD</th><th>Attempts</th><th>Action</th></tr></thead>
              <tbody>
                {filtered.map(s=>(
                  <tr key={s.tracking_id}>
                    <td><code style={{ fontSize:'.78rem', color:'var(--navy)', fontWeight:700 }}>{s.tracking_id}</code></td>
                    <td><span style={{ padding:'2px 8px', borderRadius:10, fontSize:'.65rem', fontWeight:700, background:(STATUS_COLOR[s.status]||'#888')+'20', color:STATUS_COLOR[s.status]||'#888' }}>{s.status?.replace(/_/g,' ')}</span></td>
                    <td className="text-sm">{s.service_type}</td>
                    <td className="text-sm">{typeof s.pickup_address==='object'?s.pickup_address?.city:''}</td>
                    <td className="text-sm">{typeof s.delivery_address==='object'?s.delivery_address?.city:''}</td>
                    <td className="text-sm" style={{ color:s.edd&&new Date(s.edd)<new Date()?'var(--danger)':undefined }}>{s.edd?fmtDate(s.edd):'—'}</td>
                    <td style={{ textAlign:'center' }}>{s.attempts||0}</td>
                    <td><div style={{ display:'flex', gap:4 }}>
                      <Link to={'/track/'+s.tracking_id} className="btn btn-outline btn-sm" style={{ fontSize:'.7rem' }}>View</Link>
                      <button className="btn btn-danger btn-sm" style={{ fontSize:'.7rem' }}
                        onClick={async()=>{ if(!window.confirm('Initiate RTO for '+s.tracking_id+'?')) return; try{ await api.patch('/shipments/'+s.tracking_id+'/status',{ status:'RTO_INITIATED', notes:'RTO initiated from exception report' }); toast.success('RTO initiated'); setExceptions(prev=>prev.filter(x=>x.tracking_id!==s.tracking_id)); }catch(e){ toast.error('Failed'); } }}>
                        RTO
                      </button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="✅" title="No exceptions found" />}
        </div>
      </div>
    </AppShell>
  );
}

// ── MP-05 / What-if Scenario Analysis ────────────────────────────────────────
export function ScenarioAnalysisPage() {
  const [scenario, setScenario] = useState({ volumeChange:0, priceChange:0, costChange:0, rtoPct:0 });
  const [baseline, setBaseline] = useState(null);
  useEffect(() => {
    api.get('/dashboard/executive').then(r=>r.data).then(r=>{ if(r.success) setBaseline(r.data); }).catch(()=>{});
  }, []);

  const rev = parseFloat(baseline?.revenue?.mtd||0);
  const adjRevenue  = rev * (1 + scenario.volumeChange/100) * (1 + scenario.priceChange/100);
  const adjCost     = rev * 0.6 * (1 + scenario.costChange/100);
  const rtoImpact   = rev * (scenario.rtoPct/100) * 0.15;
  const adjProfit   = adjRevenue - adjCost - rtoImpact;
  const baseProfit  = rev * 0.4;
  const profitDelta = adjProfit - baseProfit;

  const set = k => e => setScenario(s=>({...s,[k]:parseFloat(e.target.value)||0}));

  return (
    <AppShell title="What-If Scenario Analysis">
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        <div className="card">
          <div className="card-header"><span className="card-title">Input Variables</span></div>
          <div className="card-body">
            {[
              ['+/- Volume Change (%)', 'volumeChange', -50, 200],
              ['+/- Price Change (%)',  'priceChange',  -30, 50],
              ['+/- Cost Change (%)',   'costChange',   -20, 100],
              ['RTO Rate (%)',          'rtoPct',         0, 40],
            ].map(([l,k,min,max]) => (
              <div key={k} className="form-group">
                <label className="form-label" style={{ display:'flex', justifyContent:'space-between' }}>
                  <span>{l}</span>
                  <strong style={{ color:scenario[k]>0?'var(--success)':scenario[k]<0?'var(--danger)':'inherit' }}>
                    {scenario[k]>0?'+':''}{scenario[k]}%
                  </strong>
                </label>
                <input type="range" style={{ width:'100%' }} min={min} max={max} step={1}
                  value={scenario[k]} onChange={set(k)}/>
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <div className="card-header"><span className="card-title">Projected Outcome (MTD)</span></div>
          <div className="card-body">
            <div style={{ display:'grid', gap:12 }}>
              {[
                ['Baseline Revenue', fmtPKR(rev), 'var(--gray-500)'],
                ['Projected Revenue', fmtPKR(adjRevenue), adjRevenue>rev?'var(--success)':'var(--danger)'],
                ['Projected Cost', fmtPKR(adjCost), 'var(--warning)'],
                ['RTO Impact', '-'+fmtPKR(rtoImpact), 'var(--danger)'],
                ['Projected Profit', fmtPKR(adjProfit), adjProfit>baseProfit?'var(--success)':'var(--danger)'],
                ['Profit Delta', (profitDelta>=0?'+':'')+fmtPKR(profitDelta), profitDelta>=0?'var(--success)':'var(--danger)'],
              ].map(([l,v,c])=>(
                <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'10px 0', borderBottom:'1px solid var(--gray-100)' }}>
                  <span style={{ fontSize:'.85rem', color:'var(--gray-600)' }}>{l}</span>
                  <span style={{ fontWeight:700, color:c }}>{v}</span>
                </div>
              ))}
              <div style={{ padding:'12px', background: adjProfit>baseProfit?'#F0FDF4':'#FEF2F2', borderRadius:8, textAlign:'center', marginTop:8 }}>
                <div style={{ fontWeight:700, color:adjProfit>baseProfit?'var(--success)':'var(--danger)', fontSize:'1rem' }}>
                  {adjProfit>baseProfit ? '↑ Profitable Scenario' : '↓ Loss Scenario'}
                </div>
                <div style={{ fontSize:'.78rem', color:'var(--gray-500)', marginTop:4 }}>
                  Net margin: {rev>0?((adjProfit/adjRevenue)*100).toFixed(1):0}%
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
